import { Alert, Flex, Form, Spin, Typography } from "antd";
import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate, useParams } from "react-router-dom";

import { CompanyForm } from "components";
import { useStores } from "stores";
import { editCompany, getStatusMessage } from "services";
import {
  BankAccountCard,
  COMPANY_STATUS_ENUM,
  COMPANY_TYPES,
  EditCompanyRequest,
  NOTIFICATION_TYPE_ENUM,
} from "interfaces";
import { routes } from "constants/routes";
import { dateFormat, dateISOFormat, getPhoneMaskValue, parseErrorMessage } from "utils";
import { useNotificationContext } from "providers";
import dayjs from "dayjs";

export const CompanyEditPage = observer(() => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openNotification } = useNotificationContext();
  const { companyCard, bankAccountsStore, bankAccountsDetailStore } = useStores();

  const [loading, setLoading] = useState(false);
  const [defaultBankAccount, setDefaultBankAccount] = useState<BankAccountCard | null>(null);

  const [form] = Form.useForm();

  const hideStatementBlock = companyCard.card.status !== COMPANY_STATUS_ENUM.ACTIVE;

  useEffect(() => {
    if (id) {
      setLoading(true);

      const fetchCompanyData = async () => {
        try {
          await companyCard.fetchCompanyCardById(id);

          const bankAccounts = await bankAccountsStore.fetchBankAccounts(id);

          const defaultAccount = bankAccounts?.find((account) => account.isDefault);

          if (defaultAccount?.id) {
            const bankDetails = await bankAccountsDetailStore.fetchBankAccountDetails(id, defaultAccount.id);

            if (bankDetails) {
              setDefaultBankAccount(bankDetails);
              form.setFieldsValue({
                bankAccountRequest: {
                  accountNumber: bankDetails.accountNumber,
                },
              });
            }
          }
        } catch (e: any) {
          openNotification({
            description: parseErrorMessage(e),
            type: NOTIFICATION_TYPE_ENUM.ERROR,
          });
        } finally {
          setLoading(false);
        }
      };

      fetchCompanyData();
    }
  }, [id, companyCard, bankAccountsStore, bankAccountsDetailStore, openNotification, hideStatementBlock, form]);

  const closeResultModal = () => {
    return Promise.resolve(navigate(`/${routes.companies}/${companyCard.card.id}`));
  };

  const saveCompany = async () => {
    setLoading(true);
    const values = form.getFieldsValue();

    const dataToSend: EditCompanyRequest = {
      ...values,
      phone: getPhoneMaskValue(values.phone) || undefined,

      contractNumber: values.contractNumber ? values.contractNumber : undefined,
      email: values.email ? values.email : undefined,
      isOnlyTaxResidentRF: values.isOnlyTaxResidentRF ? values.isOnlyTaxResidentRF : false,
      isControlPersonsRF:
        values.type === COMPANY_TYPES.COMPANY
          ? values.isControlPersonsRF
            ? values.isControlPersonsRF
            : false
          : undefined,
      isForeignOrg:
        values.type === COMPANY_TYPES.COMPANY ? (values.isForeignOrg ? values.isForeignOrg : false) : undefined,

      bankAccountRequest: undefined,
      updateBankAccountRequest: values.bankAccountRequest
        ? {
            accountName: values.bankAccountRequest.bankName,
            ...values.bankAccountRequest,
          }
        : undefined,
      isResident: values.type === COMPANY_TYPES.IP ? true : values.isResident,
      contractDate: values.contractDate ? dateISOFormat(values.contractDate) : undefined,
      birthDate: values.birthDate ? dayjs(values.birthDate).format("YYYY-MM-DD") : undefined,
      identificationDocIssueDate: values.identificationDocIssueDate
        ? dayjs(values.identificationDocIssueDate).format("YYYY-MM-DD")
        : undefined,
      patronymic: values.patronymic ? values.patronymic : undefined,
    };

    await editCompany({ id: companyCard.card.id }, dataToSend)
      .then(() => {
        openNotification({
          message: "Данные успешно обновлены",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
        setTimeout(() => {
          closeResultModal();
          setLoading(false);
        }, 1500);
      })
      .catch((e) => {
        openNotification({
          description: parseErrorMessage(e),
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
        setLoading(false);
      });
  };

  const initialValues: EditCompanyRequest = {
    companyName: companyCard.card.companyName,
    companyRegistrationNumber: companyCard.card.companyRegistrationNumber,
    inn: companyCard.card.inn,
    kpp: companyCard.card.kpp ? companyCard.card.kpp : undefined,
    role: companyCard.card.role,
    address: companyCard.card.address,
    isResident: companyCard.card.isResident ? companyCard.card.isResident : true,
    type: companyCard.card.type,
    initialBankBik: defaultBankAccount?.bik,
    correspondentAccount: defaultBankAccount?.correspondentAccount
      ? defaultBankAccount.correspondentAccount
      : undefined,
    phone: companyCard.card.phone ? companyCard.card.phone : undefined,
    email: companyCard.card.email ? companyCard.card.email : undefined,
    contractNumber: companyCard.card.contractNumber ? companyCard.card.contractNumber : undefined,
    contractDate: companyCard.card.contractDate ? companyCard.card.contractDate : undefined,
    isOnlyTaxResidentRF: companyCard.card.isOnlyTaxResidentRF ? companyCard.card.isOnlyTaxResidentRF : false,
    isControlPersonsRF:
      companyCard.card.type === COMPANY_TYPES.COMPANY
        ? companyCard.card.isControlPersonsRF
          ? companyCard.card.isControlPersonsRF
          : false
        : undefined,
    isForeignOrg:
      companyCard.card.type === COMPANY_TYPES.COMPANY
        ? companyCard.card.isForeignOrg
          ? companyCard.card.isForeignOrg
          : false
        : undefined,
    identificationDocSeria: companyCard.card.identificationDocSeria
      ? companyCard.card.identificationDocSeria
      : undefined,
    identificationDocNumber: companyCard.card.identificationDocNumber
      ? companyCard.card.identificationDocNumber
      : undefined,
    identificationDocIssuer: companyCard.card.identificationDocIssuer
      ? companyCard.card.identificationDocIssuer
      : undefined,
    identificationDocIssueDate: companyCard.card.identificationDocIssueDate
      ? dayjs(companyCard.card.identificationDocIssueDate).format("YYYY-MM-DD")
      : undefined,
    identificationDocIssuerCode: companyCard.card.identificationDocIssuerCode
      ? companyCard.card.identificationDocIssuerCode
      : undefined,
    surname: companyCard.card.surname ? companyCard.card.surname : undefined,
    name: companyCard.card.name ? companyCard.card.name : undefined,
    patronymic: companyCard.card.patronymic ? companyCard.card.patronymic : undefined,
    birthDate: companyCard.card.birthDate ? dayjs(companyCard.card.birthDate).format("YYYY-MM-DD") : undefined,
    postAddress: companyCard.card.postAddress ? companyCard.card.postAddress : undefined,
  };

  return (
    <Spin spinning={loading || companyCard.isPending}>
      {companyCard.fetchStatus === "success" && (
        <Flex vertical>
          <Typography.Title>Редактировать данные компании</Typography.Title>
          <CompanyForm
            hideStatementBlock={hideStatementBlock}
            form={form}
            onFinish={saveCompany}
            closeHandler={closeResultModal}
            disabled={loading}
            initValues={initialValues}
            companyStatus={companyCard.card.status}
            editMode={true}
          />
        </Flex>
      )}
      {companyCard.fetchStatus === "error" && companyCard.fetchError && (
        <Alert
          message={getStatusMessage(companyCard.fetchError.status)}
          description={parseErrorMessage(companyCard.fetchError)}
          type="error"
          showIcon
        />
      )}
    </Spin>
  );
});
